import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowUpRight, ArrowRight, Menu, X, Mail, Phone, Plus } from "lucide-react";
import { BRAND, IMAGES, SEGMENTS, STATS, PARTNERS } from "@/pages/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div
    className={className}
    variants={fadeUp}
    custom={i}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
  >
    {children}
  </motion.div>
);

/* ---------------- NAV ---------------- */
const NAV_LINKS = [
  { label: "Expertise", href: "#expertise" },
  { label: "About", href: "#founder" },
  { label: "Contact", href: "#contact" },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center">
          <img src={BRAND.logo} alt={BRAND.name} className="h-9 md:h-10 w-auto object-contain" />
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm font-semibold uppercase tracking-widest text-white/70 hover:text-[#ccff00] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-cta-button"
            className="bg-[#ccff00] text-black font-bold uppercase tracking-wide text-sm px-6 py-3 hover:bg-white transition-colors"
          >
            Book a call
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-lg font-display font-bold uppercase tracking-wide text-white/80"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="bg-[#ccff00] text-black font-bold uppercase tracking-wide text-center px-6 py-3"
              >
                Book a call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ---------------- HERO ---------------- */
function Hero() {
  return (
    <section id="top" className="relative min-h-screen flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="Stadium at night" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
      </div>

      <div className="relative z-10 w-full px-6 md:px-12 lg:px-24 pb-20 md:pb-28">
        <Reveal>
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-[#ccff00]">
            <span className="w-8 h-px bg-[#ccff00]" /> {BRAND.tagline}
          </span>
        </Reveal>

        <Reveal i={1}>
          <h1 className="font-display font-semibold uppercase tracking-normal leading-[0.95] text-white mt-6 text-6xl sm:text-7xl lg:text-[8.5vw] max-w-6xl">
            Architects of<br />
            sporting <span className="text-[#ccff00]">excellence.</span>
          </h1>
        </Reveal>

        <Reveal i={2}>
          <p className="mt-8 max-w-xl text-lg md:text-xl text-white/70 leading-relaxed">
            Built from both sides of the deal. We construct strategic partnerships that
            elevate brands, empower athletes, and dominate the commercial sports landscape.
          </p>
        </Reveal>

        <Reveal i={3}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 bg-[#ccff00] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white transition-colors"
            >
              Start the conversation
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#expertise"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/25 text-white font-bold uppercase tracking-wider px-8 py-4 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
            >
              What we do
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- EXPERTISE ---------------- */
function Expertise() {
  return (
    <section id="expertise" className="relative py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 border-t border-white/10">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#ccff00]">— What we do</span>
              <h2 className="font-display font-bold uppercase tracking-tight text-5xl md:text-6xl mt-5 leading-none">
                Our<br />Expertise
              </h2>
              <p className="mt-6 text-white/60 leading-relaxed max-w-sm">
                Three sides of the table, one relentless standard. Explore how we operate for
                each part of the sports economy.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-16">
          {SEGMENTS.map((seg, si) => (
            <Reveal key={seg.id} i={si}>
              <div>
                <div className="flex items-baseline gap-4 mb-2">
                  <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#ccff00]">
                    {seg.kicker}
                  </span>
                </div>
                <h3 className="font-display font-bold uppercase tracking-tight text-2xl md:text-4xl mb-4">
                  {seg.title}
                </h3>
                <Accordion type="single" collapsible className="w-full" data-testid={`accordion-${seg.id}`}>
                  {seg.items.map((item, idx) => (
                    <AccordionItem
                      key={idx}
                      value={`${seg.id}-${idx}`}
                      className="border-b border-white/10"
                    >
                      <AccordionTrigger
                        data-testid={`accordion-trigger-${seg.id}-${idx}`}
                        className="font-display text-xl md:text-2xl font-bold uppercase tracking-tight py-6 hover:text-[#ccff00] hover:no-underline transition-colors text-left"
                      >
                        {item.title}
                      </AccordionTrigger>
                      <AccordionContent className="text-white/60 pb-6 text-base md:text-lg leading-relaxed max-w-2xl">
                        {item.desc}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FOUNDER ---------------- */
function Founder() {
  return (
    <section id="founder" className="relative py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 border-t border-white/10 bg-[#0a0a0a]">
      <Reveal>
        <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#ccff00]">— Who's behind {BRAND.name}</span>
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mt-8 items-stretch">
        <Reveal className="lg:col-span-5" i={1}>
          <div className="relative h-full min-h-[420px]">
            <div className="absolute -top-3 -left-3 w-full h-full border border-[#ccff00]/40" />
            <img
              src={IMAGES.founder}
              alt="Gerard Gómez"
              className="relative w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500 min-h-[420px]"
            />
          </div>
        </Reveal>

        <div className="lg:col-span-7 flex flex-col justify-center">
          <Reveal i={2}>
            <h2 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl leading-none">
              Gerard Gómez
            </h2>
            <p className="mt-6 text-white/65 text-lg leading-relaxed max-w-2xl">
              A relentless dealmaker with over 7 years in the trenches of global sports commerce.
              Gerard has architected some of the most recognizable partnerships in the industry,
              operating at the intersection of culture, sport, and capital — from managing activations
              on the ground to leading business development at the highest level.
            </p>
          </Reveal>

          <div className="grid grid-cols-3 gap-4 md:gap-8 mt-12 border-t border-white/10 pt-10">
            {STATS.map((s, i) => (
              <Reveal key={s.label} i={i}>
                <div data-testid={`stat-${i}`}>
                  <div className="font-display font-black text-4xl md:text-6xl text-[#ccff00] tracking-tight leading-none">
                    {s.value}
                  </div>
                  <div className="mt-3 text-xs md:text-sm uppercase tracking-widest text-white/50">
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Partner marquee */}
      <div className="mt-20 border-t border-white/10 pt-12">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-white/40 mb-8">Trusted across the ecosystem</p>
        <div className="vk-marquee overflow-hidden" data-testid="partner-marquee">
          <div className="vk-marquee-track gap-16 pr-16 items-center">
            {[...PARTNERS, ...PARTNERS].map((p, i) =>
              p.logo ? (
                <img
                  key={i}
                  src={p.logo}
                  alt={p.name}
                  className={`${p.big ? "h-16 md:h-20" : "h-12 md:h-14"} w-auto object-contain opacity-70 hover:opacity-100 transition-opacity shrink-0`}
                />
              ) : (
                <span
                  key={i}
                  className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight text-white/40 hover:text-[#ccff00] transition-colors whitespace-nowrap shrink-0"
                >
                  {p.name}
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CONTACT ---------------- */
function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry received. We'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 text-white py-4 px-0 focus:outline-none focus:border-[#ccff00] transition-colors placeholder:text-white/30 text-lg";

  return (
    <section id="contact" className="relative py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 border-t border-white/10">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#ccff00]">— Ready to build?</span>
            <h2 className="font-display font-black uppercase tracking-tighter text-6xl md:text-8xl mt-5 leading-[0.85]">
              Make your<br /><span className="text-[#ccff00]">move.</span>
            </h2>
            <p className="mt-8 text-white/60 text-lg max-w-md leading-relaxed">
              No commitment. Just a conversation about where sport fits into your strategy.
            </p>

            <div className="mt-12 space-y-6">
              <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="w-11 h-11 flex items-center justify-center border border-white/20 group-hover:border-[#ccff00] group-hover:text-[#ccff00] transition-colors">
                  <Mail size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/40">Email</div>
                  <div className="text-lg group-hover:text-[#ccff00] transition-colors">{BRAND.email}</div>
                </div>
              </a>
              <a href={`tel:${BRAND.phone.replace(/[\s()]/g, "")}`} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="w-11 h-11 flex items-center justify-center border border-white/20 group-hover:border-[#ccff00] group-hover:text-[#ccff00] transition-colors">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/40">Phone</div>
                  <div className="text-lg group-hover:text-[#ccff00] transition-colors">{BRAND.phone}</div>
                </div>
              </a>
              <a href={`tel:${BRAND.phoneAlt.replace(/[\s()]/g, "")}`} data-testid="contact-phone-alt" className="flex items-center gap-4 group">
                <span className="w-11 h-11 flex items-center justify-center border border-white/20 group-hover:border-[#ccff00] group-hover:text-[#ccff00] transition-colors">
                  <Phone size={18} />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/40">Phone (UK)</div>
                  <div className="text-lg group-hover:text-[#ccff00] transition-colors">{BRAND.phoneAlt}</div>
                </div>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal i={1}>
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-10">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/40">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Jordan Rivera"
                  data-testid="contact-input-name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/40">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  data-testid="contact-input-email"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/40">Your Objective</label>
                <textarea
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us what you're trying to build..."
                  data-testid="contact-input-message"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="group inline-flex items-center justify-center gap-2 bg-[#ccff00] text-black font-bold uppercase tracking-wider px-10 py-4 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Submit Inquiry"}
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- FOOTER ---------------- */
function Footer() {
  return (
    <footer className="border-t border-white/10 px-6 md:px-12 lg:px-24 pt-10 pb-24 md:pb-28">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <img src={BRAND.logo} alt={BRAND.name} className="h-8 w-auto object-contain" />
        <p className="text-xs uppercase tracking-widest text-white/40">
          © {new Date().getFullYear()} {BRAND.name}. Global Sports Advisory.
        </p>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <main className="relative z-[2]">
      <Navbar />
      <Hero />
      <Expertise />
      <Founder />
      <Contact />
      <Footer />
    </main>
  );
}
